import { useCallback, useRef, useState } from 'react';

const isTouchEvent = (event: MouseEvent | TouchEvent): event is TouchEvent => {
  return 'touches' in event;
};

const preventDefault = (event: MouseEvent | TouchEvent) => {
  if (!isTouchEvent(event)) return;

  if (event.touches.length < 2 && event.preventDefault) {
    event.preventDefault();
  }
};

interface LongPressOptions {
  shouldPreventDefault?: boolean;
  delay?: number;
}

export default function useLongPress(
  onLongPress: (event: React.MouseEvent | React.TouchEvent) => void,
  onClick: (event: React.MouseEvent | React.TouchEvent) => void,
  { shouldPreventDefault = true, delay = 300 }: LongPressOptions = {},
) {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef<number | null>(null);
  const target = useRef<EventTarget | null>(null);

  const start = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      if (shouldPreventDefault && event.target) {
        (event.target as HTMLElement).addEventListener(
          'touchend',
          preventDefault,
          {
            passive: false,
          },
        );
        target.current = event.target;
      }

      timeout.current = window.setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault],
  );

  const clear = useCallback(
    (event: React.MouseEvent | React.TouchEvent, shouldTriggerClick = true) => {
      if (timeout.current) {
        window.clearTimeout(timeout.current);
        timeout.current = null;
      }

      if (shouldTriggerClick && !longPressTriggered) {
        onClick(event);
      }

      setLongPressTriggered(false);

      if (shouldPreventDefault && target.current) {
        (target.current as HTMLElement).removeEventListener(
          'touchend',
          preventDefault,
        );
      }
    },
    [shouldPreventDefault, onClick, longPressTriggered],
  );

  return {
    onMouseDown: (e: React.MouseEvent) => start(e),
    onTouchStart: (e: React.TouchEvent) => start(e),
    onMouseUp: (e: React.MouseEvent) => clear(e),
    onMouseLeave: (e: React.MouseEvent) => clear(e, false),
    onTouchEnd: (e: React.TouchEvent) => clear(e),
  };
}
